import { InsightFilterEnum } from 'appRedux/models/favoritesModels';

type InsightBucket = {
  m_insight_value?: number | null;
  m_insight_ntile?: number | null;
  w_insight_value?: number | null;
  w_insight_ntile?: number | null;
  d_insight_value?: number | null;
  d_insight_ntile?: number | null;
  h8_insight_value?: number | null;
  h8_insight_ntile?: number | null;
  h4_insight_value?: number | null;
  h4_insight_ntile?: number | null;
  h1_insight_value?: number | null;
  h1_insight_ntile?: number | null;
};

export type CompareTrendSlot = {
  insights?: Partial<Record<string, InsightBucket | null | undefined>> | null;
};

const RTP_VALUE_KEYS = [
  'm_insight_value',
  'w_insight_value',
  'd_insight_value',
  'h8_insight_value',
  'h4_insight_value',
  'h1_insight_value',
] as const;

/** Positive finite RTP values across timeframes — same filter GradientLineChart plots. */
export function rtpPositiveValueCount(slot: CompareTrendSlot | null | undefined): number {
  const insight = slot?.insights?.[InsightFilterEnum.ReturnToPlayer];
  if (!insight) return 0;
  let count = 0;
  for (const key of RTP_VALUE_KEYS) {
    const v = insight[key];
    if (typeof v === 'number' && Number.isFinite(v) && v > 0) count += 1;
  }
  return count;
}

/**
 * A machine "has RTP" for Compare trend purposes only when it has enough
 * positive RTP *values* to draw a line (≥2 points). Ntile-only / zeros /
 * missing `return` do NOT count — that was why #109 still showed a line for
 * machines Miller considers without RTP.
 */
export function slotHasRtp(slot: CompareTrendSlot | null | undefined): boolean {
  return rtpPositiveValueCount(slot) >= 2;
}

export function allComparedMachinesHaveRtp(slots: CompareTrendSlot[] | null | undefined): boolean {
  if (!slots?.length) return false;
  return slots.every((slot) => slotHasRtp(slot));
}

/**
 * Final display gate for the Compare GradientLineChart overlay.
 * - RTP must be among the selected Compare metrics (otherwise the line is
 *   positioned at the default 22% offset over Jackpots / first row).
 * - Every compared machine must have drawable RTP values.
 */
export function shouldShowCompareRtpTrend(args: {
  slots: CompareTrendSlot[] | null | undefined;
  rtpMetricSelected: boolean;
}): boolean {
  if (!args.rtpMetricSelected) return false;
  return allComparedMachinesHaveRtp(args.slots);
}

/** Build GradientLineChart trendValues from a slot's `return` insight. */
export function buildRtpTrendValues(slot: CompareTrendSlot | null | undefined) {
  const insight = slot?.insights?.[InsightFilterEnum.ReturnToPlayer];
  return {
    monthTrendValue: {
      value: insight?.m_insight_value,
      ntile: insight?.m_insight_ntile,
    },
    weekTrendValue: {
      value: insight?.w_insight_value,
      ntile: insight?.w_insight_ntile,
    },
    dayTrendValue: {
      value: insight?.d_insight_value,
      ntile: insight?.d_insight_ntile,
    },
    eightHoursTrendValue: {
      value: insight?.h8_insight_value,
      ntile: insight?.h8_insight_ntile,
    },
    fourHoursTrendValue: {
      value: insight?.h4_insight_value,
      ntile: insight?.h4_insight_ntile,
    },
    oneHourTrendValue: {
      value: insight?.h1_insight_value,
      ntile: insight?.h1_insight_ntile,
    },
  };
}
